import clsx from 'clsx';
import React, { ButtonHTMLAttributes, ReactNode } from 'react';

import { Icon } from '~/components/icon/Icon';
import { SvgIconNames } from '~/design-tokens/iconography/SvgIcons';

import styles from './Button.module.scss';

export type Size = 'xsmall' | 'small' | 'medium' | 'large' | 'xl' | 'xxl';
export type IconPosition = 'left' | 'right';
export type Variant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'empty'
  | 'critical';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  ref?: React.Ref<HTMLButtonElement>;
  label?: ReactNode;
  iconName?: SvgIconNames;
  iconPosition?: IconPosition;
  variant?: Variant;
  size?: Size;
  isLoading?: boolean;
  fullWidth?: boolean;
  'data-testid'?: string;
  children?: ReactNode;
}

export const Button = ({
  label,
  iconName,
  iconPosition = 'left',
  variant = 'primary',
  size = 'medium',
  isLoading = false,
  fullWidth = false,
  className,
  children,
  ref,
  ...rest
}: ButtonProps) => {
  let iconSize: number;

  switch (size) {
    case 'large':
    case 'xl':
      iconSize = 20;
      break;
    case 'xxl':
      iconSize = 40;
      break;
    case 'xsmall':
      iconSize = 12;
      break;
    default:
      iconSize = 16;
  }

  return (
    <button
      data-button
      data-testid="button"
      className={clsx(
        styles.root,
        styles[variant],
        styles[size],
        className,
        {
          [styles.fullWidth]: fullWidth,
          [styles.iconOnly]: !label,
        }
      )}
      {...rest}
      ref={ref}
    >
      {isLoading ? (
        <span className={styles.loading} data-testid="button-loading">
          <div className={styles.spinner} />
        </span>
      ) : (
        <span
          data-button-label
          className={clsx(styles.label, {
            [styles.reversed]: iconPosition === 'right',
          })}
        >
          {iconName && <Icon iconName={iconName} size={iconSize} />}
          {label && (
            <span data-button-text className={styles.text}>
              {label}
            </span>
          )}
        </span>
      )}
      {children}
    </button>
  );
};
